import React, { useState, useEffect } from "react";
import { useQuery, useMutation } from "@apollo/client";
import {
  LIST_AUTO_RESPONDERS,
  ATTACH_AUTO_RESPONDER_TO_CONVERSATION,
  DETACH_AUTO_RESPONDER_FROM_CONVERSATION,
} from "../../services/graphql/auto-responder-operations";
import { CharacterLimitInput } from "../auto-responder/character-limit-input";

import type { AutoResponder } from "../../types/auto-responder";

interface AutoResponderSelectorProps {
  conversationId?: string;
  selectedAutoResponderId?: string;
  onAutoResponderChange: (autoResponderId: string | null) => void;
  disabled?: boolean;
  showPreview?: boolean;
}

export function AutoResponderSelector({
  conversationId,
  selectedAutoResponderId,
  onAutoResponderChange,
  disabled = false,
  showPreview = true,
}: AutoResponderSelectorProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const [showCreateForm, setShowCreateForm] = useState(false);

  // Query auto-responders
  const { data, loading, error, refetch } = useQuery(LIST_AUTO_RESPONDERS, {
    variables: {
      filter: {
        isActive: true,
        search: searchQuery || undefined,
      },
      limit: 50,
    },
    fetchPolicy: "cache-and-network",
  });

  // Mutations for attachment/detachment
  const [attachAutoResponder] = useMutation(
    ATTACH_AUTO_RESPONDER_TO_CONVERSATION,
    {
      onCompleted: () => {
        refetch();
      },
      onError: (error) => {
        console.error("Error attaching auto-responder:", error);
      },
    }
  );

  const [detachAutoResponder] = useMutation(
    DETACH_AUTO_RESPONDER_FROM_CONVERSATION,
    {
      onCompleted: () => {
        refetch();
      },
      onError: (error) => {
        console.error("Error detaching auto-responder:", error);
      },
    }
  );

  const autoResponders = data?.listAutoResponders?.items || [];
  const selectedAutoResponder = autoResponders.find(
    (ar: AutoResponder) => ar.id === selectedAutoResponderId
  );

  const handleAutoResponderSelect = async (autoResponderId: string | null) => {
    if (disabled) return;

    // If we have a conversation ID, handle attachment/detachment
    if (conversationId) {
      try {
        if (
          selectedAutoResponderId &&
          selectedAutoResponderId !== autoResponderId
        ) {
          // Detach current auto-responder
          await detachAutoResponder({
            variables: {
              autoResponderId: selectedAutoResponderId,
              conversationId,
            },
          });
        }

        if (autoResponderId) {
          // Attach new auto-responder
          await attachAutoResponder({
            variables: {
              autoResponderId,
              conversationId,
            },
          });
        }
      } catch (error) {
        console.error("Error managing auto-responder attachment:", error);
        return;
      }
    }

    onAutoResponderChange(autoResponderId);
  };

  const filteredAutoResponders = autoResponders.filter(
    (autoResponder: AutoResponder) => {
      if (!searchQuery) return true;
      const query = searchQuery.toLowerCase();
      return (
        autoResponder.name.toLowerCase().includes(query) ||
        autoResponder.content.toLowerCase().includes(query) ||
        autoResponder.tags.some((tag) => tag.toLowerCase().includes(query))
      );
    }
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
        <p className="text-red-600">
          Error loading auto-responders: {error.message}
        </p>
        <button
          onClick={() => refetch()}
          className="mt-2 text-red-600 hover:text-red-700 font-medium"
        >
          Try again
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="auto-responder-selector">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-medium text-gray-900">Auto-Responder</h3>
        <span className="text-sm text-gray-500">(Optional - 1 max)</span>
      </div>

      {/* Search */}
      <div className="relative">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <svg
            className="h-5 w-5 text-gray-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
            />
          </svg>
        </div>
        <input
          type="text"
          placeholder="Search auto-responders..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
          disabled={disabled}
          data-testid="auto-responder-search"
        />
      </div>

      {/* Auto-Responder List */}
      <div className="space-y-2 max-h-96 overflow-y-auto">
        {filteredAutoResponders.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <svg
              className="w-12 h-12 mx-auto mb-4 text-gray-300"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
              />
            </svg>
            <p>No auto-responders found</p>
            {searchQuery && (
              <p className="text-sm mt-1">Try adjusting your search terms</p>
            )}
          </div>
        ) : (
          filteredAutoResponders.map((autoResponder: AutoResponder) => (
            <div
              key={autoResponder.id}
              className={`border rounded-lg p-3 cursor-pointer transition-colors ${
                selectedAutoResponderId === autoResponder.id
                  ? "border-green-300 bg-green-50"
                  : "border-gray-200 hover:border-gray-300 hover:bg-gray-50"
              } ${disabled ? "opacity-50 cursor-not-allowed" : ""}`}
              onClick={() =>
                !disabled && handleAutoResponderSelect(autoResponder.id)
              }
              data-testid={`auto-responder-option-${autoResponder.id}`}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <h4 className="font-medium text-gray-900">
                    {autoResponder.name}
                  </h4>
                  <p className="text-sm text-gray-600 mt-1">
                    {autoResponder.content}
                  </p>

                  {/* Tags */}
                  {autoResponder.tags.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {autoResponder.tags.map((tag) => (
                        <span
                          key={tag}
                          className="px-2 py-1 text-xs bg-gray-100 text-gray-600 rounded-full"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}

                  {/* Device Actions and Stats */}
                  <div className="flex items-center justify-between mt-2 text-xs text-gray-500">
                    <div className="flex items-center space-x-3">
                      <span>
                        Mobile:{" "}
                        {autoResponder.deviceActions?.mobileAction || "SMS"}
                      </span>
                      <span>
                        Desktop:{" "}
                        {autoResponder.deviceActions?.desktopAction || "CHAT"}
                      </span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <span>{autoResponder.content.length}/130 chars</span>
                      <span>
                        {autoResponder.conversationIds.length} conversations
                      </span>
                    </div>
                  </div>
                </div>

                {/* Selection indicator */}
                {selectedAutoResponderId === autoResponder.id && (
                  <div className="ml-3 text-green-600">
                    <svg
                      className="w-5 h-5"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      {/* Preview Section */}
      {showPreview && selectedAutoResponder && (
        <div className="border-t pt-4">
          <h4 className="font-medium text-gray-900 mb-3">Message Preview</h4>
          <div className="space-y-3">
            {/* Mobile Preview */}
            <div className="bg-gray-50 rounded-lg p-3">
              <div className="flex items-center space-x-2 mb-2">
                <svg
                  className="w-4 h-4 text-gray-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z"
                  />
                </svg>
                <span className="text-sm font-medium text-gray-700">
                  Mobile (SMS)
                </span>
              </div>
              <div className="bg-white rounded p-2 text-sm">
                {selectedAutoResponder.content} https://activqms.com/t/abc123
              </div>
              <div className="text-xs text-gray-500 mt-1">
                ~{selectedAutoResponder.content.length + 25} characters (SMS
                limit: 160)
              </div>
            </div>

            {/* Desktop Preview */}
            <div className="bg-gray-50 rounded-lg p-3">
              <div className="flex items-center space-x-2 mb-2">
                <svg
                  className="w-4 h-4 text-gray-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                  />
                </svg>
                <span className="text-sm font-medium text-gray-700">
                  Desktop (Chat)
                </span>
              </div>
              <div className="bg-blue-500 text-white rounded-lg p-2 text-sm max-w-xs">
                {selectedAutoResponder.content}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
